import React from "react";
import { FileText, Eye, ExternalLink, FileImage, FileCode, FileVideo, Box, File, Archive } from "lucide-react";
import { cn } from "@/dentlab/src/lib/utils/cn";
import { RecentFile } from "@/lib/utils/recents";
import { FileType } from "@/lib/utils/file-utils";

interface RecentFilesWidgetProps {
	files: RecentFile[];
	onOpenFile: (fileId: string) => void;
	onNavigateToJob: (clientId: string, jobId: number) => void;
	/** When set, shows an "Archiv" entry point in the header (client context only). */
	onOpenArchive?: () => void;
	className?: string;
}

const getFileIcon = (fileType: FileType) => {
	switch (fileType) {
		case "pdf":
			return <FileText className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />;
		case "img":
			return <FileImage className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />;
		case "xml":
			return <FileCode className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />;
		case "video":
			return <FileVideo className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />;
		case "3d":
			return <Box className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />;
		default:
			return <File className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />;
	}
};

export const RecentFilesWidget: React.FC<RecentFilesWidgetProps> = ({
	files,
	onOpenFile,
	onNavigateToJob,
	onOpenArchive,
	className,
}) => {
	return (
		<div
			className={cn(
				"bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col",
				className
			)}
			data-testid="dashboard-recent-files"
		>
			<div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 flex-shrink-0">
				<FileText className="h-4 w-4 text-gray-400" />
				<span className="text-sm font-semibold text-gray-900">
					Zuletzt angesehene Dateien
				</span>
				{onOpenArchive && (
					<button
						type="button"
						className="ml-auto flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
						onClick={onOpenArchive}
						aria-label="Archiv dieses Kunden öffnen"
						data-testid="recent-files-open-archive"
					>
						<Archive className="h-3.5 w-3.5" />
						Archiv
					</button>
				)}
			</div>

			<div className="border-t border-gray-200" />

			{files.length === 0 ? (
				<div className="flex-1 flex items-center justify-center">
					<span className="text-sm text-gray-400">
						Keine zuletzt angesehenen Dateien
					</span>
				</div>
			) : (
				<div className="flex-1 min-h-0 overflow-y-auto flex flex-col px-3 py-1">
					{files.map((file) => (
						<div
							key={file.fileId}
							className="flex items-center gap-2 py-1.5 px-1 border-b border-gray-100 last:border-b-0"
							data-testid={`recent-file-${file.fileId}`}
							aria-label={`Datei ${file.fileName}`}
						>
							{getFileIcon(file.fileType)}
							<span className="text-[13px] text-gray-700 truncate flex-1 min-w-0">
								{file.fileName}
							</span>
							<div className="flex items-center gap-0.5 flex-shrink-0">
								<button
									type="button"
									onClick={() => onOpenFile(file.fileId)}
									className="p-1 rounded hover:bg-gray-100 transition-colors"
									aria-label={`${file.fileName} ansehen`}
									data-testid={`recent-file-view-${file.fileId}`}
								>
									<Eye className="h-3.5 w-3.5 text-gray-400" />
								</button>
								<button
									type="button"
									onClick={() => {
										if (file.clientId && file.jobId) {
											onNavigateToJob(
												file.clientId,
												file.jobId
											);
										}
									}}
									disabled={!file.clientId || !file.jobId}
									className="p-1 rounded hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
									aria-label={`Zum Auftrag von ${file.fileName} navigieren`}
									data-testid={`recent-file-navigate-${file.fileId}`}
								>
									<ExternalLink className="h-3.5 w-3.5 text-gray-400" />
								</button>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
};
